import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ventes as api } from '../services/api';
import { useFiltresVentes, usePanneau, eurCourt, pct1, basculer } from './ventesUtils';
import AvancementMois from './AvancementMois';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles, sectionsGroupes, basculerGroupe } from './regroupements';
import FiltresPanel from './FiltresPanel';
import './Ventes.css';
import './Stock.css';   // st-tools / st-seg du réglage d'affichage
import './VentesJour.css';

// ─── VN — Le mois au jour le jour ────────────────────────────────────────────
// Gabarit commun (CLAUDE.md) : panneau frère de <main>, vt-page, vt-kpis,
// vt-card. Aucune barre de couleur dans les cartes.
//
// ⚠ CE QUI SE COMPTE EST DONNÉ AU JOUR, CE QUI EST UN TAUX EST DONNÉ EN CUMUL.
// Le VN livre 11,7 véhicules par jour en moyenne : à ce volume la pénétration
// Roole ne peut prendre que douze valeurs, et son écart-type quotidien (24,3
// points) vaut deux fois l'amplitude entre le meilleur et le pire mois. Les
// trois taux — marge, Roole, financement — sont donc ceux du CUMUL depuis le
// 1er, où ils se stabilisent dès le quatrième jour. Le taux du jour reste
// accessible en infobulle, pour qui veut vérifier.
//
// ⚠ `date_vente` est la date de LIVRAISON : la mise en circulation tombe le
// même jour dans 75 % des cas. Ce n'est pas la commande — les colonnes de date
// de commande de la source sont vides.

const nfr = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nfr.format(Math.round(v))} €`);
const pct = (v) => (v == null ? '—' : pct1(v));

const jourCourt = (s) => {
  const d = new Date(`${s}T00:00:00`);
  return `${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${d.getDate()}`;
};
const estWeekend = (s) => [0, 6].includes(new Date(`${s}T00:00:00`).getDay());
const moisLong = (ym) => {
  const [a, m] = ym.split('-');
  return new Date(Number(a), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const moisPrecedents = (n) => {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};
// ⚠ Un rapport quotidien se lit sur le mois en cours, et au plus sur le
// précédent pour comparer. Les treize mois en liste de radios occupaient la
// moitié du panneau pour un usage rare : les plus anciens passent dans une
// liste déroulante, sans rien perdre.
const MOIS_RECENTS = moisPrecedents(2);
const MOIS_CHOIX = [
  { code: '', label: 'Mois en cours' },
  { code: MOIS_RECENTS[1], label: moisLong(MOIS_RECENTS[1]) },
];
const MOIS_AUTRES = moisPrecedents(13).slice(2).map((m) => ({ code: m, label: moisLong(m) }));

// ─── Le bloc de répartition : quatre axes, une mécanique ────────────────────
// Les quatre champs sont déjà dans le détail envoyé par le serveur ; changer
// d'axe ne coûte donc aucune requête. Chacun a SA clé d'URL : choisir une
// marque puis basculer sur les vendeurs ne doit pas transformer le filtre
// marque en filtre vendeur — ils se cumulent, et le panneau les montre.
// `st` (2026-09-30) : la même clé lue sur une ligne du STOCK (cube marque ×
// modèle × type × tranche d'âge envoyé par le serveur) — présente sur les
// seuls axes qu'un véhicule en stock possède. `vnSeul` : en VO l'export n'a
// pas de ligne de modèle, sa désignation complète ferait un millier de lignes.
const cleModele = (marque, ligne) => `${marque || '—'} · ${ligne || '— NR'}`;
const AXES_VENTE = [
  { c: 'marque', lib: 'Par marque', col: 'Marque', cle: 'marques', f: (v) => v.marque, st: (r) => r.marque },
  { c: 'modele', lib: 'Par modèle', col: 'Modèle', cle: 'modeles', vnSeul: true,
    f: (v) => cleModele(v.marque, v.modeleLigne), st: (r) => cleModele(r.marque, r.modele) },
  { c: 'vendeur', lib: 'Par vendeur', col: 'Vendeur', cle: 'vendeurs', f: (v) => v.vendeur || '—' },
  { c: 'famille', lib: 'Par type de client', col: 'Type de client', cle: 'familles', f: (v) => v.famille || '—' },
  { c: 'segment', lib: 'Par type de stock', col: 'Type de stock', cle: 'segments', f: (v) => v.segment || '—', st: (r) => r.segment || '—' },
  // L'ÂGE DU VÉHICULE AU MOMENT DE LA VENTE. Mêmes bornes que le rapport
  // Stock : les deux écrans se lisent ensemble — ce qu'on a en rayon, et l'âge
  // de ce qu'on vend. Des bornes différentes rendraient la comparaison
  // impossible.
  { c: 'age', lib: 'Par âge de stock', col: 'Âge de stock', cle: 'ages', ordonne: true, st: (r) => r.age,
    f: (v) => (v.joursStock == null ? 'âge inconnu'
      : v.joursStock <= 60 ? '≤ 60 j'
        : v.joursStock <= 120 ? '61-120 j'
          : v.joursStock <= 365 ? '121-365 j' : '> 365 j') },
];
// Un axe ordonné se lit dans son ordre, jamais par volume décroissant.
const ORDRE_AGE = ['≤ 60 j', '61-120 j', '121-365 j', '> 365 j', 'âge inconnu'];

// Colonnes du tableau, en trois groupes : ce qui s'est passé le jour même,
// puis ce que le mois donne à date. Mélanger les deux sans le dire serait le
// meilleur moyen de faire lire un taux quotidien comme une performance.
const COLS = [
  // ⚠ Les BDC viennent du CRM (Gestion commerciale), pas de Datacar : Datacar
  // ne connaît que le FACTURÉ. C'est la colonne qui permet de voir l'activité
  // AVANT la livraison — et la date retenue est celle de signature du bon.
  { c: 'bdc', g: 'Le jour', lib: 'BDC', f: (j) => j.bdc, fmt: (v) => (v ? nfr.format(v) : '·'), si: 'bdc' },
  // Livrés = ventes NETTES (factures − avoirs). La colonne « Annul. » est
  // retirée (2026-09-28, demande du user) : placée à côté, elle se lisait
  // comme un nombre encore à soustraire, alors qu'il l'est déjà.
  { c: 'volume', g: 'Le jour', lib: 'Livrés', sous: 'ventes nettes', f: (j) => j.volume, fmt: (v) => nfr.format(v), fort: true },
  { c: 'ca', g: 'Le jour', lib: 'CA HT', f: (j) => j.ca, fmt: eur },
  { c: 'marge', g: 'Le jour', lib: 'Marge', f: (j) => j.marge, fmt: eur, si: 'marge' },
  { c: 'txMarge', g: 'Cumul du mois', lib: 'Taux de marge', f: (j) => j.txMarge, fmt: pct, discret: true, jour: (j) => j.txMargeJour, si: 'marge' },
  { c: 'roole', g: 'Cumul du mois', lib: 'Roole', sous: 'particuliers', f: (j) => j.roole, fmt: pct, discret: true, jour: (j) => j.rooleJour, si: 'roole' },
  { c: 'financement', g: 'Cumul du mois', lib: 'Financement', sous: '÷ CA TTC', f: (j) => j.financement, fmt: pct, discret: true, si: 'financement' },
];
const groupes = (cols) => cols.reduce((acc, c) => {
  const d = acc[acc.length - 1];
  if (d && d.lib === c.g) d.n += 1; else acc.push({ lib: c.g, n: 1 });
  return acc;
}, []);

// Agrège un ensemble de lignes de détail. C'est la SEULE définition utilisée
// par l'écran — pour le mois entier comme pour un jour sélectionné — afin que
// les deux vues ne puissent pas diverger. Vérifiée contre les totaux du
// serveur : volume, Roole et financement tombent au centième.
//
// ⚠ `roole` porte le SIGNE (−1 souscription annulée, 0 aucune, +1 souscrite),
// pas un booléen : sans lui le taux sortait 71,6 % au lieu de 70,7 %.
// ⚠ Roole se calcule sur les PARTICULIERS seuls (2026-10-01), numérateur et
// dénominateur : `volPart` et `roole` ne comptent que leurs lignes.
function agreger(lignes) {
  const a = lignes.reduce((x, l) => ({
    volume: x.volume + l.sens,
    volPart: x.volPart + (l.particulier ? l.sens : 0),
    annulations: x.annulations + (l.sens < 0 ? 1 : 0),
    ca: x.ca + l.ca, caTtc: x.caTtc + l.caTtc, marge: x.marge + l.marge,
    // Taux de marge hors ventes en mode agent (règle du panorama groupe).
    catx: x.catx + (l.agent ? 0 : l.ca), mgtx: x.mgtx + (l.agent ? 0 : l.marge),
    roole: x.roole + (l.particulier ? l.roole : 0), finance: x.finance + l.finance,
  }), { volume: 0, volPart: 0, annulations: 0, ca: 0, caTtc: 0, marge: 0, catx: 0, mgtx: 0, roole: 0, finance: 0 });
  return {
    ...a,
    txMarge: a.catx ? (a.mgtx / a.catx) * 100 : null,
    rooleTx: a.volPart > 0 ? (a.roole / a.volPart) * 100 : null,
    financementTx: a.caTtc ? (a.finance / a.caTtc) * 100 : null,
  };
}

const MARQ = [
  // Même place qu'au quotidien : le bon de commande PRÉCÈDE la livraison, la
  // colonne le précède aussi.
  { c: 'bdc', lib: 'BDC', f: (m) => m.bdc, fmt: (v) => (v ? nfr.format(v) : '·'), si: 'bdc' },
  { c: 'volume', lib: 'Livrés', f: (m) => m.volume, fmt: (v) => (v ? nfr.format(v) : '·'), fort: true },
  // Stock du jour en regard des livraisons (2026-09-30) ; colonne retirée sur
  // les axes qu'un stock n'a pas (vendeur, type de client).
  { c: 'stock', lib: 'Stock', f: (m) => m.stock, fmt: (v) => (v ? nfr.format(v) : '·'), si: 'stock' },
  { c: 'ca', lib: 'CA HT', f: (m) => m.ca, fmt: eurCourt },
  { c: 'marge', lib: 'Marge', f: (m) => m.marge, fmt: eurCourt, si: 'marge' },
  { c: 'txMarge', lib: 'Taux de marge', f: (m) => m.txMarge, fmt: pct, discret: true, si: 'marge' },
  { c: 'rooleTx', lib: 'Roole', f: (m) => m.rooleTx, fmt: pct, discret: true, si: 'roole' },
  { c: 'financementTx', lib: 'Financement', f: (m) => m.financementTx, fmt: pct, discret: true, si: 'financement' },
];

// Détail véhicule. ⚠ Aucun nom de client : `marts.ventes_vn` en porte un, mais
// cette base n'est pas pseudonymisée et A97 BI n'est pas déclaré comme
// traitement de données personnelles. `Famille` est une catégorie, pas une
// personne.
const DET = [
  { c: 'marque', lib: 'Marque', f: (v) => v.marque, l: true, fort: true },
  { c: 'modele', lib: 'Modèle', f: (v) => v.modele, l: true },
  { c: 'vendeur', lib: 'Vendeur', f: (v) => v.vendeur || '—', l: true, discret: true },
  { c: 'famille', lib: 'Famille', f: (v) => v.famille || '—', l: true, discret: true },
  { c: 'ca', lib: 'CA HT', f: (v) => v.ca, fmt: eur },
  { c: 'marge', lib: 'Marge', f: (v) => v.marge, fmt: eur, si: 'marge' },
  { c: 'tx', lib: 'Taux', f: (v) => (v.ca && !v.agent ? (v.marge / v.ca) * 100 : null), fmt: pct, discret: true, si: 'marge' },
  { c: 'joursStock', lib: 'Stock', sous: 'jours', f: (v) => v.joursStock, fmt: (x) => (x == null ? '—' : `${nfr.format(x)} j`), discret: true },
  { c: 'roole', lib: 'Roole', f: (v) => v.roole, fmt: (x) => (x > 0 ? '✓' : x < 0 ? 'annulé' : '·'), si: 'roole' },
  { c: 'finance', lib: 'Financé', f: (v) => v.finance, fmt: (x) => (x ? eur(x) : '·'), discret: true, si: 'financement' },
];

// ⚠ Un même écran pour quatre périmètres : VN/VO × Antilles/Métropole. Ce ne
// sont pas des copies — les sources ne portent pas les mêmes champs, et le
// serveur dit lesquels sont réellement remplis (`data.colonnes`). Les colonnes
// absentes disparaissent au lieu d'afficher des zéros : en VN métropole la
// marge n'est pas publiée, et Roole comme le financement sont des produits
// antillais, à 0 % partout ailleurs.
export default function VentesJour({ type = 'vn', branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [dispo, setDispo] = useState([]);
  // Jour sélectionné : null = tout le mois. Le clic sur une ligne filtre les
  // marques ET le détail — c'est ce qui fait passer l'écran du tableau de bord
  // à l'outil de travail.
  const [jourSel, setJourSel] = useState(null);
  const f = useFiltresVentes(dispo);
  const [ouvert, ouvrirPanneau] = usePanneau();
  const mois = f.moisStr;
  const societes = f.societesUrl;
  // La marque n'a pas de filtre serveur : le mois entier est déjà dans le
  // détail, elle se filtre à l'écran. Mais elle vit dans l'URL et se reflète
  // dans le panneau (section « catégories »), comme tout autre filtre —
  // un seul état, visible (CLAUDE.md §4 bis).
  // Axe affiché dans le bloc de répartition — réglage d'affichage, donc en
  // en-tête de carte (CLAUDE.md §2), pas dans le panneau.
  const [axe, setAxe] = useState('marque');
  const AXE = AXES_VENTE.find((a) => a.c === axe) || AXES_VENTE[0];
  // Une sélection par axe, chacune dans sa propre clé d'URL. Elles se cumulent.
  const selDe = (a) => { const u = f.params.get(a.cle) || ''; return u ? u.split(',').filter(Boolean) : []; };
  const axeSel = selDe(AXE);
  const selections = AXES_VENTE.map((a) => ({ a, sel: selDe(a) })).filter((x) => x.sel.length > 0);
  const clesActives = selections.length;

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const { data: d } = await api.jour(type, { mois, societes, branche });
      setData(d);
      setDispo(d.societesDispo || []);
      setJourSel(null);   // un changement de mois ou de périmètre remet à plat
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
      setData(null);
    }
  }, [mois, societes, type, branche]);
  useEffect(() => { charger(); }, [charger]);

  // Chaque bloc subit les AUTRES filtres, jamais le sien : le tableau par
  // marque reste complet quand on choisit une marque (c'est la liste où l'on
  // choisit), mais il suit le jour. Idem pour le tableau quotidien.
  const tout = useMemo(() => data?.detail || [], [data]);
  const parJourOk = (v) => !jourSel || v.jour === jourSel;
  // Règle 4 : un bloc ne se filtre pas lui-même. `saufAxe` applique tous les
  // filtres SAUF celui de l'axe courant — c'est la liste où l'on choisit.
  const okSauf = (v, exclu) => selections.every((x) => x.a.c === exclu || x.sel.includes(x.a.f(v)));
  const cleFiltres = AXES_VENTE.map((a) => f.params.get(a.cle) || '').join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lignesSaufAxe = useMemo(() => tout.filter((v) => parJourOk(v) && okSauf(v, axe)), [tout, jourSel, cleFiltres, axe]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lignesSaufJour = useMemo(() => tout.filter((v) => okSauf(v, null)), [tout, cleFiltres]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lignes = useMemo(() => tout.filter((v) => parJourOk(v) && okSauf(v, null)), [tout, jourSel, cleFiltres]);
  const A = useMemo(() => agreger(lignes), [lignes]);

  // ─── Bons de commande : cumuls par société et par marque ──────────────────
  // ⚠ La source ne partage QUE la marque avec les ventes livrées. Dès qu'un
  // filtre porte sur une dimension qu'elle ignore — vendeur (jamais poussé par
  // le CRM, donnée personnelle), type de client, type de stock, âge —, le
  // nombre de bons ne peut pas suivre le rétrécissement des livraisons.
  // Afficher un total inchangé à côté de volumes filtrés ferait lire un écart
  // qui n'existe pas : la colonne disparaît alors, et le bloc le dit.
  const bdcLignes = useMemo(() => data?.bdc?.lignes || [], [data]);
  const marquesSel = selDe(AXES_VENTE[0]);
  const filtreHorsPortee = selections.some((x) => x.a.c !== 'marque');
  // ⚠ La colonne reste affichée même à zéro, à la demande de l'utilisateur :
  // « rien de signé aujourd'hui » est une information, et une colonne qui
  // apparaît et disparaît selon l'activité se lit comme une panne. Elle ne
  // s'efface que dans les cas où elle serait IMPOSSIBLE (filtre hors portée).
  const bdcUtilisable = !!data?.bdc?.dispo && !filtreHorsPortee;
  // Les bons retenus : le jour sélectionné, et le filtre marque s'il y en a un.
  const bdcRetenus = useMemo(
    () => bdcLignes.filter((b) => (!jourSel || b.jour === jourSel)
      && (marquesSel.length === 0 || marquesSel.includes(b.marque))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bdcLignes, jourSel, cleFiltres]);
  const sommeBdc = (l) => l.reduce((t, b) => t + b.n, 0);
  // Pendant de `M` : le mois entier, filtres compris, sans la sélection de jour.
  const bdcMois = useMemo(
    () => sommeBdc(bdcLignes.filter((b) => marquesSel.length === 0 || marquesSel.includes(b.marque))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bdcLignes, cleFiltres]);
  const bdcParSociete = useMemo(() => {
    const m = new Map();
    bdcRetenus.forEach((b) => m.set(b.societeCode, (m.get(b.societeCode) || 0) + b.n));
    return m;
  }, [bdcRetenus]);
  // Règle 4 : le bloc par marque ne se filtre pas lui-même — il ignore donc la
  // sélection de marques, mais suit le jour.
  const bdcParMarque = useMemo(() => {
    const m = new Map();
    bdcLignes.filter((b) => !jourSel || b.jour === jourSel)
      .forEach((b) => m.set(b.marque, (m.get(b.marque) || 0) + b.n));
    return m;
  }, [bdcLignes, jourSel]);
  // Vue d'ensemble pour la direction générale : le même jeu d'indicateurs, par
  // concession. Elle suit la sélection comme le reste de la page — un directeur
  // qui clique une journée veut savoir quelle concession a livré ce jour-là.
  const concessions = useMemo(() => {
    const m = new Map();
    lignes.forEach((v) => {
      if (!m.has(v.societe)) m.set(v.societe, { code: v.societeCode, master: v.master, plaque: v.plaque, l: [] });
      m.get(v.societe).l.push(v);
    });
    return [...m.entries()]
      .map(([societe, o]) => ({ societe, libelle: societe, code: o.code, master: o.master, plaque: o.plaque,
        ...agreger(o.l), bdc: bdcParSociete.get(o.code) || 0 }))
      .filter((x) => x.volume !== 0 || x.bdc > 0);
    // ⚠ `x.volume !== 0 || x.bdc > 0` : une concession qui a pris des commandes
    // sans encore livrer doit apparaître — c'est précisément ce que ce rapport
    // sert à voir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignes, bdcParSociete]);
  // Maille d'affichage du bloc par concession : concession, site, plaque.
  const maille = useRegroupement();
  const axes = useMemo(() => axesDisponibles(concessions), [concessions]);
  // ⚠ On regroupe en re-sommant les LIGNES de vente, pas les agrégats déjà
  // calculés : les taux (marge, Roole, financement) sont des rapports, et les
  // moyenner donnerait un chiffre faux dès que deux concessions n'ont pas le
  // même volume. `agreger` est la seule fonction d'agrégation de l'écran.
  const sommerConcessions = (membres) => {
    const codes = new Set(membres.map((m) => m.code));
    return {
      ...agreger(lignes.filter((v) => codes.has(v.societeCode))),
      bdc: sommeBdc(bdcRetenus.filter((b) => codes.has(b.societeCode))),
    };
  };
  const arbreConcessions = useMemo(
    () => construireArbre(concessions, maille.niveau, sommerConcessions, maille.ouverts, (x) => x.volume),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [concessions, maille.niveau, maille.ouverts, lignes]);
  // Cliquer une concession PILOTE LE FILTRE DU PANNEAU plutot que d'ajouter un
  // second mecanisme : la page se recharge sur ce perimetre, les cases du
  // panneau suivent, et il n'y a qu'un seul etat de filtrage a comprendre.
  // Recliquer la seule concession retenue revient a tout le perimetre.

  // ─── Le stock en regard ──────────────────────────────────────────────────
  // Même règle 4 que les ventes : le cube subit les filtres des AUTRES axes que
  // le stock connaît (marque, modèle, type, âge) — jamais celui de l'axe
  // courant, ni le jour (c'est une photo). Vendeur et type de client, qu'un
  // stock n'a pas, ne le filtrent pas : l'en-tête le dit.
  const stockAxe = !!data?.stock?.dispo && !!AXE.st;
  const stockParCle = useMemo(() => {
    const m = new Map();
    if (!stockAxe) return m;
    (data.stock.lignes || [])
      .filter((r) => selections.every((x) => x.a.c === axe || !x.a.st || x.sel.includes(x.a.st(r))))
      .forEach((r) => { const k = AXE.st(r); m.set(k, (m.get(k) || 0) + r.n); });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, axe, cleFiltres, stockAxe]);
  const stockNonFiltre = stockAxe && selections.some((x) => !x.a.st);

  const repartition = useMemo(() => {
    const m = new Map();
    lignesSaufAxe.forEach((v) => { const k = AXE.f(v); if (!m.has(k)) m.set(k, []); m.get(k).push(v); });
    const l = [...m.entries()]
      .map(([cle, x]) => ({ cle, ...agreger(x), bdc: axe === 'marque' ? (bdcParMarque.get(cle) || 0) : null,
        stock: stockAxe ? (stockParCle.get(cle) || 0) : null }))
      .filter((x) => x.volume !== 0);
    // Les références en stock sans livraison ce mois : c'est souvent là qu'est
    // le sujet — elles apparaissent, à zéro livré.
    if (stockAxe) {
      const vues = new Set(l.map((x) => x.cle));
      [...stockParCle.entries()].sort((a, b) => b[1] - a[1]).forEach(([cle, n]) => {
        if (!vues.has(cle)) l.push({ cle, ...agreger([]), bdc: axe === 'marque' ? 0 : null, stock: n });
      });
    }
    // ⚠ Une marque COMMANDÉE mais pas encore livrée n'apparaît dans aucune
    // ligne de vente : sans cet ajout, le signal d'amont — celui que ce
    // rapport existe pour montrer — serait précisément le seul à manquer.
    if (axe === 'marque') {
      const vues = new Set(l.map((x) => x.cle));
      bdcParMarque.forEach((n, cle) => {
        if (!vues.has(cle)) l.push({ cle, ...agreger([]), bdc: n });
      });
    }
    return AXE.ordonne
      ? l.sort((a, b) => ORDRE_AGE.indexOf(a.cle) - ORDRE_AGE.indexOf(b.cle))
      : l.sort((a, b) => b.volume - a.volume || (b.stock || 0) - (a.stock || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignesSaufAxe, axe, stockParCle]);
  // Règle 3 : la liste des marques du panneau vient du mois ENTIER (au périmètre
  // société et jour près), jamais du résultat filtré — sinon la marque choisie
  // serait la seule proposée et on ne pourrait plus en changer.
  // Le tableau quotidien est recalculé ici pour qu'il suive le filtre marque —
  // le serveur ne sait pas filtrer par marque et n'a pas à le savoir, le mois
  // entier est déjà dans le détail. Sans filtre, il retombe exactement sur
  // `data.jours` (contrôlé au navigateur).
  const J = useMemo(() => {
    const parJ = new Map();
    lignesSaufJour.forEach((v) => { if (!parJ.has(v.jour)) parJ.set(v.jour, []); parJ.get(v.jour).push(v); });
    // ⚠ Ce tableau est RECONSTRUIT ici à partir des livraisons pour suivre le
    // filtre marque : il remplace `data.jours`, et perdait donc le nombre de
    // bons que le serveur y avait posé — la colonne BDC affichait « — » sur
    // toutes les lignes.
    const bdcJ = new Map();
    bdcLignes.filter((b) => marquesSel.length === 0 || marquesSel.includes(b.marque))
      .forEach((b) => bdcJ.set(b.jour, (bdcJ.get(b.jour) || 0) + b.n));
    // ⚠ Un jour où l'on a SIGNÉ sans livrer n'existe dans aucune ligne de
    // vente : sans cette union, la colonne ne pourrait rien montrer ce jour-là.
    // Jours ouvrés sans vente (2026-10-30, comme APV) : fournis par le serveur,
    // présents à zéro, marqués « aucune facture ». Les compteurs de jours de
    // vente (N, dernier jour livré) ne retiennent que les jours avec ventes.
    const sansVente = new Set(data?.joursSansVente || []);
    const jours = [...new Set([...parJ.keys(), ...bdcJ.keys(), ...sansVente])].sort();
    let c = [];
    return jours.map((jour) => {
      const l = parJ.get(jour) || [];
      c = c.concat(l);
      const j = agreger(l); const cum = agreger(c);
      return {
        jour, bdc: bdcJ.get(jour) || 0, sansFacture: sansVente.has(jour) && !l.length && !bdcJ.get(jour),
        volume: j.volume, annulations: j.annulations, ca: j.ca, marge: j.marge,
        txMargeJour: j.txMarge, rooleJour: j.rooleTx,
        txMarge: cum.txMarge, roole: cum.rooleTx, financement: cum.financementTx,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignesSaufJour, bdcLignes, cleFiltres, data?.joursSansVente]);
  // Le repère « dernier jour livré » désigne une LIVRAISON : un jour qui ne
  // porte que des bons ne doit pas le recevoir.
  const dernierLivre = useMemo(() => {
    for (let i = J.length - 1; i >= 0; i -= 1) if (J[i].volume !== 0) return i;
    return -1;
  }, [J]);
  // Ce que la source porte vraiment sur ce périmètre, mesuré par le serveur.
  const COLONNES = {
    ...(data?.colonnes || { marge: true, roole: true, financement: true }),
    // La colonne BDC n'existe que si l'entrepôt porte des bons de commande sur
    // ce périmètre. Une colonne de zéros ferait croire à une absence d'activité
    // commerciale, alors qu'il s'agirait d'une absence de SAISIE. Elle
    // s'efface aussi sous un filtre que la source ne sait pas honorer.
    bdc: bdcUtilisable,
  };
  // Le repère du mois : les ventes n'ont pas d'objectif saisi, leur référence
  // est le mois précédent à périmètre égal (cf. services/ventes-jour.js).
  const R = data?.repere || { cible: 0, attendu: null, courbe: 'lineaire' };
  const garde = (cols) => cols.filter((c) => !c.si || COLONNES[c.si]);
  // ⚠ `bdcUtilisable` DOIT figurer en dépendance : sans lui, les listes ne se
  // recalculaient qu'au rechargement des données, et la colonne BDC restait
  // affichée sous un filtre que la source ne sait pas honorer — exactement le
  // cas qu'elle est censée couvrir.
  const COLS_V = useMemo(() => garde(COLS), [data, bdcUtilisable]);   // eslint-disable-line react-hooks/exhaustive-deps
  const MARQ_V = useMemo(() => garde(MARQ), [data, bdcUtilisable]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ⚠ Hors de l'axe marque, la colonne BDC n'est pas « vide » : elle est
  // IMPOSSIBLE — un bon de commande n'a ni vendeur poussé, ni type de client,
  // ni âge de stock. On la retire plutôt que d'aligner des tirets.
  const MARQ_AXE = useMemo(() => MARQ.filter((c) => (c.c === 'stock' ? stockAxe : (!c.si || COLONNES[c.si])))
    .filter((c) => c.c !== 'bdc' || axe === 'marque'),
    [MARQ_V, axe, stockAxe]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ⚠ La colonne retirée doit DIRE pourquoi. Sans note, son absence se lit
  // comme un oubli — c'est exactement ce qui a été signalé le 2026-09-20.
  const bdcVentilable = axe === 'marque';
  const DET_V = useMemo(() => garde(DET), [data]);        // eslint-disable-line react-hooks/exhaustive-deps

  const valeursDispo = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => [...new Set([...lignesSaufAxe.map(AXE.f), ...stockParCle.keys()])].sort((a, b) => (AXE.ordonne
      ? ORDRE_AGE.indexOf(a) - ORDRE_AGE.indexOf(b)
      : a.localeCompare(b, 'fr'))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lignesSaufAxe, axe, stockParCle]);
  // Règle 2 : recliquer la seule valeur retenue revient au périmètre complet.
  const choisir = (val) => f.setFiltre({ [AXE.cle]: basculer(f.params.get(AXE.cle) || '', val) });

  const Panneau = (
    <FiltresPanel
      ouvert={ouvert} onToggle={ouvrirPanneau}
      periodes={MOIS_CHOIX} periode={mois} onPeriode={(m) => f.setFiltre({ mois: m })}
      moisAutres={MOIS_AUTRES}
      groupes={sectionsGroupes(concessions)}
      onToggleGroupe={(g) => f.setFiltre({ societes: basculerGroupe(g, f.selected, data?.societesDispo || dispo) })}
      categoriesApres
      categoriesDispo={valeursDispo.map((m) => ({ code: m, nom: m }))}
      categoriesActives={axeSel} onToggleCategorie={choisir} titreCategories={AXE.col}
      societesDispo={data?.societesDispo || dispo} societesActives={f.selected}
      onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })}
      onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe}
      nbActifs={(mois ? 1 : 0) + (f.societesUrl ? 1 : 0) + clesActives}
      onReset={() => f.setFiltre({ societes: '', mois: '', marques: '', modeles: '', vendeurs: '', familles: '', segments: '', ages: '' })}
    />
  );

  if (erreur) return <><main className="vt-page"><div className="vj-erreur">{erreur}</div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  // ⚠ Le nombre de jours de LIVRAISON, pas le nombre de lignes du tableau :
  // depuis que les jours portant un bon sans livraison y figurent, les deux
  // diffèrent — et `N` sert aussi de dénominateur à la moyenne par jour.
  // (calcul direct : on est après un retour anticipé, pas de hook ici)
  const N = J.filter((j) => j.volume !== 0).length;
  // Le bandeau reste sur le CUMUL DU MOIS quand rien n'est sélectionné (c'est
  // l'avancement qu'on vient lire), et bascule sur le jour quand on en choisit
  // un. Les taux d'un jour sont alors annotés du nombre de livraisons qui les
  // fonde : à quatre véhicules, 100 % de Roole ne veut rien dire.
  // Règle 6 : le pied totalise LES LIGNES AFFICHÉES du tableau — donc le mois
  // filtré par marque, jamais par le jour sélectionné.
  const M = agreger(lignesSaufJour);
  const C = jourSel ? A : M;            // la sélection — bandeau d'indicateurs
  const surN = jourSel ? `sur ${nfr.format(Math.abs(A.volume))} livraison${Math.abs(A.volume) > 1 ? 's' : ''}` : 'sur le cumul du mois';

  // Moyenne par jour LIVRÉ, pas par jour du mois : compter les jours sans
  // livraison écraserait la moyenne sans rien dire de l'activité.
  const moy = N ? C.volume / N : 0;

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>{type === 'vo' ? 'VO' : 'VN'} — Le mois au jour le jour</h1>
          </div>
          <div className="vt-asof"><b>{moisLong(data.mois)}</b></div>
        </div>

        {N === 0 ? (
          <div className="vj-vide">
            {data.vide
              ? 'Aucune société sélectionnée — cochez-en au moins une dans les filtres, à droite.'
              : 'Aucune livraison sur ce mois.'}
          </div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: `repeat(${3 + (COLONNES.marge ? 2 : 0) + (COLONNES.roole ? 1 : 0) + (COLONNES.financement ? 1 : 0)},1fr)` }}>
              <div className="vt-kpi">
                <div className="l">Véhicules livrés{jourSel && <span className="vj-filtre">jour</span>}</div>
                <div className="v">{nfr.format(C.volume)}</div>
                <div className="d vj-d">
                  {jourSel ? jourCourt(jourSel) : `${N} jour${N > 1 ? 's' : ''} de livraison · ${moy.toFixed(1)} par jour`}
                </div>
              </div>
              <div className="vt-kpi">
                <div className="l">CA HT</div>
                <div className="v">{eurCourt(C.ca)}</div>
                <div className="d vj-d">{eur(C.volume ? C.ca / C.volume : null)} par véhicule</div>
              </div>
              {COLONNES.marge && (
                <div className="vt-kpi">
                  <div className="l">Marge</div>
                  <div className="v">{eurCourt(C.marge)}</div>
                  <div className="d vj-d">{eur(C.volume ? C.marge / C.volume : null)} par véhicule</div>
                </div>
              )}
              {COLONNES.marge && (
                <div className="vt-kpi">
                  <div className="l">Taux de marge</div>
                  <div className="v">{pct(C.txMarge)}</div>
                  <div className="d vj-d">{surN}</div>
                </div>
              )}
              {COLONNES.roole && (
                <div className="vt-kpi">
                  <div className="l">Pénétration Roole</div>
                  <div className="v">{pct(C.rooleTx)}</div>
                  <div className="d vj-d">souscriptions ÷ particuliers livrés · {surN}</div>
                </div>
              )}
              {COLONNES.financement && (
                <div className="vt-kpi">
                  <div className="l">Financement</div>
                  <div className="v">{pct(C.financementTx)}</div>
                  <div className="d vj-d">Crédit Moderne + Somafi ÷ CA TTC · {surN}</div>
                </div>
              )}
            </div>

            {R.cible > 0 && !jourSel && (
              <div className="vt-card">
                <div className="vt-card-h"><h2>Où en est le mois</h2>
                  <span className="hint">{N} jour{N > 1 ? 's' : ''} de livraison · repère : le mois précédent, faute d'objectif saisi</span></div>
                <AvancementMois
                  realise={C.ca} cible={R.cible} attendu={R.attendu}
                  repere="mois précédent" article="du" fmt={eurCourt}
                  jours={`${N} jour${N > 1 ? 's' : ''} de livraison`}
                  estime={R.courbe === 'lineaire'} />
              </div>
            )}

            {jourSel && (
              <div className="vj-sel">
                <b>{jourCourt(jourSel)}</b> — la répartition et le détail ci-dessous ne montrent que cette journée.
                <button type="button" className="vj-sel-x" onClick={() => setJourSel(null)}>Revenir au mois</button>
              </div>
            )}

            {(data.societesDispo || []).length > 1 && concessions.length > 0 && (
              <div className="vt-card">
                <div className="vt-card-h">
                  <h2>{jourSel ? `Par concession — ${jourCourt(jourSel)}` : 'Par concession — cumul du mois'}</h2>
                  <div className="st-tools">
                    {!COLONNES.marge && <span className="hint">marge non publiée sur ce périmètre</span>}
                    <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                  </div>
                </div>
                <div className="vj-scroll">
                  <table className="vj-tbl tbl-dense">
                    <thead>
                      <tr>
                        <th className="l">Concession</th>
                        {MARQ_V.map((col) => (
                          <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>{col.lib}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {arbreConcessions.map((c) => {
                        const cible = c.feuille ? c.code : (c.membres || []).join(',');
                        return (
                        <tr key={c.cle}
                          className={`tbl-clic rg-n${c.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                          onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                          <td className="l vj-marque">
                            <span className="rg-in" style={{ width: c.niveau * 14 }} />
                            {/* Le dépliement a son propre bouton : cliquer la LIGNE
                                filtre la page sur le regroupement entier, ce qui est
                                le geste attendu ; descendre d'un niveau est une autre
                                intention et ne doit pas se déclencher par erreur. */}
                            {c.feuille
                              ? <span className="rg-chev vide" />
                              : (
                                <button type="button" className={`rg-btn${maille.ouverts.has(c.cle) ? ' on' : ''}`}
                                  title={maille.ouverts.has(c.cle) ? 'Replier' : 'Voir le détail par concession'}
                                  aria-label="Déplier"
                                  onClick={(e) => { e.stopPropagation(); maille.basculer(c.cle); }}>▶</button>
                              )}
                            {c.libelle || c.societe}
                            {!c.feuille && <span className="rg-nb">{c.nbEnfants}</span>}
                          </td>
                          {MARQ_V.map((col) => {
                            const v = col.f(c);
                            const cls = [col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : '',
                              col.alerte && v > 0 ? 'vj-alerte' : ''].filter(Boolean).join(' ');
                            return <td key={col.c} className={cls}>{v == null ? '—' : col.fmt(v)}</td>;
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="vj-tot">
                        <td className="l">Ensemble</td>
                        {MARQ_V.map((col) => {
                          const v = col.f({ ...A, bdc: sommeBdc(bdcRetenus) });
                          return <td key={col.c} className={col.discret ? 'vj-discret' : ''}>{v == null ? '—' : col.fmt(v)}</td>;
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Le mois jour par jour</h2>
              </div>
              <div className="vj-scroll">
                <table className="vj-tbl tbl-dense">
                  <thead>
                    <tr className="vj-groupes">
                      <th className="l" />
                      {groupes(COLS_V).map((g, i) => <th key={i} colSpan={g.n}>{g.lib}</th>)}
                    </tr>
                    <tr>
                      <th className="l">{N} jour{N > 1 ? 's' : ''} de livraison</th>
                      {COLS_V.map((col) => (
                        <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>
                          {col.lib}{col.sous && <small>{col.sous}</small>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {J.map((j, i) => (
                      <tr key={j.jour}
                        className={`tbl-clic${i === dernierLivre ? ' tbl-courant' : ''}${estWeekend(j.jour) ? ' vj-weekend' : ''}${j.sansFacture ? ' aj-sans' : ''}${jourSel === j.jour ? ' actif' : ''}`}
                        onClick={() => setJourSel(jourSel === j.jour ? null : j.jour)}>
                        <td className="l vj-jour">
                          <b>{jourCourt(j.jour)}</b>
                          {estWeekend(j.jour) && <span className="vj-we">hors jour ouvré</span>}
                          {i === dernierLivre && <span className="vj-tag">dernier jour livré</span>}
                          {j.sansFacture && <span className="vj-tag aj-zero">aucune facture</span>}
                        </td>
                        {COLS_V.map((col) => {
                          const v = col.f(j);
                          const cls = [col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : '',
                            col.alerte && v > 0 ? 'vj-alerte' : ''].filter(Boolean).join(' ');
                          // Le taux DU JOUR reste consultable en infobulle : la
                          // colonne montre le cumul, mais on ne cache rien.
                          const t = col.jour ? col.jour(j) : null;
                          return (
                            <td key={col.c} className={cls}
                              title={t == null ? undefined : `Ce jour-là : ${pct(t)} (sur ${nfr.format(j.volume)} livraison${j.volume > 1 ? 's' : ''})`}>
                              {v == null ? '—' : col.fmt(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* ⚠ Ce pied était écrit EN DUR, huit cellules dans un ordre
                        figé. L'ajout de la colonne BDC décalait donc toute la
                        ligne d'un cran : le volume tombait sous « BDC », le CA
                        sous « Annul. »… Il suit désormais la MÊME liste de
                        colonnes que l'en-tête et le corps, et ne peut plus
                        diverger. */}
                    <tr className="vj-tot">
                      <td className="l">Total du mois</td>
                      {COLS_V.map((col) => {
                        // ⚠ `agreger` nomme les taux `rooleTx` et
                        // `financementTx` ; les colonnes lisent `roole` et
                        // `financement`. L'ancien pied, écrit en dur, affichait
                        // donc `M.roole` — la SOMME des signes — comme un
                        // pourcentage (31 % au lieu de 40,8 %), et un
                        // `M.financement` inexistant, donc « — ».
                        const v = col.f({ ...M, roole: M.rooleTx, financement: M.financementTx, bdc: bdcMois });
                        const cls = [col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : '',
                          col.alerte && v > 0 ? 'vj-alerte' : ''].filter(Boolean).join(' ');
                        return <td key={col.c} className={cls}>{v == null ? '—' : col.fmt(v)}</td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="vt-card">
              <div className="vt-card-h">
                <h2>{jourSel ? `${AXE.lib} — ${jourCourt(jourSel)}` : `Le mois ${AXE.lib.toLowerCase()}`}</h2>
                <div className="st-tools">
                  {COLONNES.bdc && !bdcVentilable && (
                    <span className="hint">⚠️ un bon de commande ne permet pas de ventiler par {AXE.col.toLowerCase()}</span>
                  )}
                  {stockAxe && (
                    <span className="hint">
                      stock : photo du {data.stock.arrete ? data.stock.arrete.split('-').reverse().join('/') : 'jour'}
                      {stockNonFiltre ? ', non filtrée par vendeur ni type de client' : ''}
                    </span>
                  )}
                  <div className="st-seg">
                    {AXES_VENTE.filter((a) => !a.vnSeul || type === 'vn').map((a) => {
                      // Un axe vide sur ce périmètre n'est pas proposé : le
                      // vendeur n'est pas toujours renseigné dans l'export.
                      const off = !tout.some((v) => a.f(v) !== '—');
                      return (
                        <button key={a.c} type="button" disabled={off}
                          className={axe === a.c ? 'on' : ''}
                          title={off ? `« ${a.col} » n'est renseigné sur aucune vente du mois` : ''}
                          onClick={() => setAxe(a.c)}>{a.lib.replace('Par ', '')}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* Quarante-cinq vendeurs poussaient le reste du rapport hors de
                  l'écran : le bloc défile dans son propre cadre, borné à une
                  vingtaine de lignes, en-tête collé. */}
              <div className="vj-scroll vj-scroll-borne">
                <table className="vj-tbl tbl-dense">
                  <thead>
                    <tr>
                      <th className="l">{AXE.col}</th>
                      {MARQ_AXE.map((col) => (
                        <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>{col.lib}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {repartition.map((m) => (
                      <tr key={m.cle}
                        className={`tbl-clic${axeSel.includes(m.cle) ? ' actif' : ''}`}
                        onClick={() => choisir(m.cle)}>
                        <td className="l vj-marque">{m.cle}</td>
                        {MARQ_AXE.map((col) => {
                          const v = col.f(m);
                          const cls = [col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : '',
                            col.alerte && v > 0 ? 'vj-alerte' : ''].filter(Boolean).join(' ');
                          return <td key={col.c} className={cls}>{v == null ? '—' : col.fmt(v)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="vt-card">
              <div className="vt-card-h">
                <h2>{jourSel ? `Véhicules livrés — ${jourCourt(jourSel)}` : 'Véhicules livrés du mois'}</h2>
                <span className="hint">
                  {nfr.format(lignes.length)} ligne{lignes.length > 1 ? 's' : ''}
                </span>
              </div>
              {/* Le détail peut faire cent lignes : il défile dans son propre
                  cadre, bordé à vingt lignes, plutôt que de pousser le reste du
                  rapport hors de l'écran. L'en-tête reste collé. */}
              <div className="vj-scroll vj-scroll-borne">
                <table className="vj-tbl vj-detail tbl-dense">
                  <thead>
                    <tr>
                      {!jourSel && <th className="l">Jour</th>}
                      {DET_V.map((col) => (
                        <th key={col.c} className={`${col.l ? 'l' : ''} ${col.discret ? 'vj-th-discret' : ''}`}>
                          {col.lib}{col.sous && <small>{col.sous}</small>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((v, i) => (
                      <tr key={i} className={v.sens < 0 ? 'vj-annule' : ''}>
                        {!jourSel && <td className="l vj-jour-court">{jourCourt(v.jour)}</td>}
                        {DET_V.map((col) => {
                          const x = col.f(v);
                          const cls = [col.l ? 'l' : '', col.discret ? 'vj-discret' : '',
                            col.fort ? 'vj-fort' : ''].filter(Boolean).join(' ');
                          return <td key={col.c} className={cls}>{col.fmt ? col.fmt(x) : x}</td>;
                        })}
                      </tr>
                    ))}
                    {lignes.length === 0 && (
                      <tr><td colSpan={DET.length + 1} className="vj-vide">Aucune livraison ce jour-là.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
      {Panneau}
    </>
  );
}
